/**
 * Trinity Triangle Mosaic System - Core Types
 *
 * Coordinate System:
 * - Uses axial coordinates (row, col) for triangle identification
 * - Orientation (up/down) derived from (row + col) % 2
 * - All triangles are equilateral with configurable side length
 */

export interface TriangleCoord {
  row: number;
  col: number;
}

export type TriangleOrientation = 'up' | 'down';

export interface Triangle {
  coord: TriangleCoord;
  orientation: TriangleOrientation;
  color: string;
  // Computed properties
  vertices: [Point, Point, Point];
  center: Point;
}

export interface Point {
  x: number;
  y: number;
}

export interface GridConfig {
  sideLength: number;      // Triangle side length in pixels
  originX: number;         // Grid origin X offset
  originY: number;         // Grid origin Y offset
  rows: number;            // Number of rows in grid
  cols: number;            // Number of columns in grid
}

export interface MosaicState {
  triangles: Map<string, Triangle>;  // Key: "row,col"
  gridConfig: GridConfig;
  selectedColor: string;
  hoveredCoord: TriangleCoord | null;
}

// Trinity brand colors from the logo
export const TRINITY_PALETTE = {
  lightBlue: '#1BB1E7',
  mediumBlue: '#0099DA',
  darkBlue: '#0060AF',
  deepBlue: '#143F91',
  white: '#FFFFFF',
  transparent: 'transparent',
} as const;

export type TrinityColor = keyof typeof TRINITY_PALETTE;

// === Tri-Cube Coordinate Types ===

export interface TriCubeCoord {
  a: number;
  b: number;
  c: number;
}

export type PatternFn = (a: number, b: number, c: number) => string | null;

// Edge identification for magnetic snapping
export type EdgeIndex = 0 | 1 | 2;  // Three edges per triangle

export interface Edge {
  triangle: TriangleCoord;
  edgeIndex: EdgeIndex;
  start: Point;
  end: Point;
}

// Neighbor relationships
export interface NeighborInfo {
  coord: TriangleCoord;
  sharedEdge: EdgeIndex;
}

// === Image-to-Triangle Types ===

export type ColorMode = 'pipette' | 'mosaic' | 'blue' | 'grayscale';

export type TriangleSize = 'S' | 'M' | 'L';

export interface ImageProcessorConfig {
  blurRadius: number;
  sobelThreshold: number;
  pointDensity: number;
  maxPoints: number;
  triangleSize: TriangleSize;
  colorMode: ColorMode;
  strokeWidth: number;
  strokeColor: string;
  showOriginal: boolean;
  showGrid: boolean;
  includeCorners: boolean;
  includeEdgePoints: boolean;
  customSideLength?: number;   // Overrides TRIANGLE_SIZE_PRESETS when set (5-60px)
  customLaneCount?: number;    // Overrides auto-calculated rows (0 = auto)
}

export interface CustomGridConfig {
  sideLength: number;    // 5-60px continuous range
  laneCount: number;     // 0 = auto-compute from image dims
  scaleFactor: number;   // Multiplier for pattern coordinates (default 1.0)
}

// Maps size preset to equilateral triangle side length in pixels
// Trinity System: 3 fixed sizes only (S=5, M=10, L=15)
export const TRIANGLE_SIZE_PRESETS: Record<TriangleSize, number> = {
  S: 10,
  M: 20,
  L: 30,
};

// Legacy size presets (for backward compat with image-to-triangles)
export const LEGACY_SIZE_PRESETS: Record<TriangleSize, number> = {
  S: 25,
  M: 50,
  L: 100,
};

// Pattern categories for the Trinity System
export type PatternCategory = 'icon' | 'basic' | 'image' | 'logo' | 'custom';

// Serializable pattern data for DB storage and gallery
export interface PatternData {
  id?: string;
  readableId?: string;
  name: string;
  description: string;
  category: PatternCategory;
  functionBody: string | null;
  triangles: Array<{ a: number; b: number; c: number; color: string }>;
  originA: number;
  originB: number;
  originC: number;
  width?: number;
  height?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProcessedImage {
  width: number;
  height: number;
  originalData: ImageData;
  edgePoints: Point[];
  triangles: ImageTriangle[];
  svg: string;
  timestamp: number;
  sourceFileName: string;
}

export interface ImageTriangle {
  id: string;
  vertices: [Point, Point, Point];
  color: string;
  originalColor: string;
  centroid: Point;
  selected: boolean;
  row?: number;
  col?: number;
}

export interface LibraryEntry {
  id: string;
  name: string;
  svgDataUrl: string;
  thumbnailDataUrl: string;
  sourceFileName: string;
  config: ImageProcessorConfig;
  createdAt: number;
  width: number;
  height: number;
}
